import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';
import { useAuth0 } from '@auth0/auth0-react';
import { UserContext } from '~contexts/user';

import * as REST from '~services/rest';

const needsAuthForNewMatches = (searchCriteria) =>
  searchCriteria?.['new-matches'] === true && !searchCriteria?.['notification-id'];

export const useListing = ({
  initialResults = [],
  initialCount = null,
  itemsPerPage = 12,
  debounceTime = 500,
  matchRequestName,
  countRequestName,
  matchMockFileName,
  countMockFileName,
  onNoResults = () => {},
  parseResult = (r) => {
    return r;
  },
  onCleanup = () => {},
}) => {
  const { isLoading: isLoadingAuthentication, getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { isLoaded: isLoadedUser } = useContext(UserContext);

  const [results, setResults] = useState(initialResults);
  const [count, setCount] = useState(initialCount);
  const [newMatchesTotal, setNewMatchesTotal] = useState(null);
  const [offset, setOffset] = useState(0);

  const [pendingDataRequests, setPendingDataRequests] = useState([]);
  const [pendingCountRequests, setPendingCountRequests] = useState([]);
  const [isLoadingResults, setIsLoadingResults] = useState(true);
  const [isLoadingCount, setIsLoadingCount] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const isMounted = useRef(true);
  
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const resolveRequestToken = useCallback(
    async (searchCriteria) => {
      if (!needsAuthForNewMatches(searchCriteria) || !isAuthenticated) {
        return undefined;
      }
      try {
        return await getAccessTokenSilently();
      } catch (error) {
        return undefined;
      }
    },
    [getAccessTokenSilently, isAuthenticated]
  );

  const fetchDataWithCriteria = async (searchCriteria) => {
    const requestId = uuidv4();
    const token = await resolveRequestToken(searchCriteria);
    const request = REST.get({
      name: matchRequestName,
      params: {
        ...searchCriteria,
      },
      token,
    }).then((response) => {
      if (!isMounted.current) return;
      
      if (typeof response === 'undefined' || !response.data) {
        return;
      }

      const newResults = response.data.map((r) => parseResult(r));
      setResults(newResults);
      if (typeof response.new_matches_total === 'number') {
        setNewMatchesTotal(response.new_matches_total);
      } else if (!searchCriteria?.['new-matches']) {
        setNewMatchesTotal(null);
      }
      setPendingDataRequests([]);
    });

    pendingDataRequests.forEach((r) => r.request.cancel());
    setPendingDataRequests([{ request, id: requestId }]);
  };

  const fetchCountWithCriteria = async (searchCriteria, redirectOnNoResults = false) => {
    const requestId = uuidv4();
    const token = await resolveRequestToken(searchCriteria);
    const request = REST.get({
      name: countRequestName,
      mockFileName: countMockFileName,
      params: {
        count: true,
        ...searchCriteria,
      },
      token,
    }).then((response) => {
      if (typeof response === 'undefined' || !response.data || !response.data[0]) {
        return;
      }

      setCount(response.data[0].count || 0);
      if (typeof response.new_matches_total === 'number') {
        setNewMatchesTotal(response.new_matches_total);
      }
      setPendingCountRequests([]);
      if (redirectOnNoResults && response.data[0].count === 0) {
        onNoResults();
      }
    });

    pendingCountRequests.forEach((r) => r.request.cancel());
    setPendingCountRequests([{ request, id: requestId }]);
  };

  const debouncedCriteria = useDebouncedCallback((crit) => fetchDataWithCriteria(crit), debounceTime, {
    leading: true,
  });
  
  const debouncedCount = useDebouncedCallback(
    (crit, redirectOnNoResults) => fetchCountWithCriteria(crit, redirectOnNoResults),
    debounceTime,
    { leading: true }
  );

  const updateListing = (newCriteria, redirectOnNoResults = false) => {
    debouncedCriteria(newCriteria);
    debouncedCount(newCriteria, redirectOnNoResults);
    const newOffset = newCriteria?.pagination?.offset || 0;
    setOffset(newOffset);
  };

  const reorder = (newCriteria) => {
    debouncedCriteria(newCriteria);
    const newOffset = newCriteria?.pagination?.offset || 0;
    setOffset(newOffset);
  };

  const showMore = async (newCriteria) => {
    if (isLoadingMore || offset >= count) {
      return;
    }

    setIsLoadingMore(true);
    // Resume from the end of the held list rather than the last requested offset: a list
    // restored from a previous visit carries its items but not the requests that built it.
    const newOffset = Math.max(offset + itemsPerPage, results.length);
    const criteriaWithPagination = {
      ...newCriteria,
      'pagination-limit': itemsPerPage,
      'pagination-offset': newOffset,
    };

    const token = await resolveRequestToken(criteriaWithPagination);
    REST.get({
      name: matchRequestName,
      mockFileName: matchMockFileName,
      params: {
        ...criteriaWithPagination,
      },
      token,
    }).then((response) => {
      if (!isMounted.current) return;
      
      if (typeof response === 'undefined' || !response.data || !response.data.length) {
        setIsLoadingMore(false);
        return;
      }
      const newResults = response.data.map((r) => parseResult(r));
      setResults((prevResults) => {
        const existingIds = new Set(prevResults.map((item) => item.personId || item.id));
        const dedupedNewResults = [];
        for (const item of newResults) {
          const itemId = item.personId || item.id;
          if (!existingIds.has(itemId)) {
            existingIds.add(itemId);
            dedupedNewResults.push(item);
          }
        }
        return [...prevResults, ...dedupedNewResults];
      });
      setOffset(newOffset);
      setIsLoadingMore(false);
    });
  };

  useEffect(() => {
    if (pendingDataRequests.length > 0) {
      setIsLoadingResults(true);
    } else {
      setIsLoadingResults(false);
    }
  }, [pendingDataRequests]);

  useEffect(() => {
    if (pendingCountRequests.length > 0) {
      setIsLoadingCount(true);
    } else {
      setIsLoadingCount(false);
    }
  }, [pendingCountRequests]);

  useEffect(() => {
    onCleanup();
  }, [onCleanup]);

  return {
    results,
    count,
    newMatchesTotal,
    offset,
    isLoadingResults,
    isLoadingCount,
    isLoadingMore,
    updateListing,
    reorder,
    showMore,
    setResults,
    setCount,
  };
};
